<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import ImageModal from './ImageModal.vue';
import { getBase64Image } from '../../../../../utils/CommonFunctions';
import { api } from '../../../../../api/Api';
import Toast from '../../../../../services/Toast';
import { Item } from '../../../../../types/models';
import AsyncImage from '../../../../../components/ui/form/AsyncImage.vue';

interface Image {
  id: string | number
  [key: string]: any // Allow any field name
}

const props = defineProps<{
  images: Image[]
  imageField?: string // Field name for image URL
  captionField?: string // Field name for caption
  columns?: number
  gap?: number
  item: Item
  aspectRatio?: string
  rounded?: boolean
  showCaption?: boolean
  clickable?: boolean
}>()

const emit = defineEmits<{
  (e: 'click', image: Image): void
  (e: 'removed', data: any[]): void
}>()

const showModal = ref(false)
const currentImageIndex = ref(0)

// reactive map to hold resolved (string) image URLs per image id
const imageSrcs = ref<Record<string | number, string>>({})

const loadImageSrc = async (image: Image) => {
  const field = props.imageField || 'src'
  const original = image[field] || ''
  if (!original) {
    imageSrcs.value[image.id] = ''
    return
  }
  try {
    const im = await getBase64Image(original)
    if (im && im.data) {
      imageSrcs.value[image.id] = im.data
    } else {
      imageSrcs.value[image.id] = original
    }
  } catch {
    imageSrcs.value[image.id] = original
  }
}

// synchronous getter used in template (returns a string)
const getImageUrl = (image: Image) => {
  return image[props.imageField || 'src'] || ''
}

const getCaption = (image: Image) => {
  return image[props.captionField || 'caption'] || ''
}


const gridColumns = computed(() => {
  return `repeat(${props.columns || 3}, minmax(0, 1fr))`
})

const gridGap = computed(() => {
  return `${props.gap || 4}px`
})

const imageContainerClasses = computed(() => {
  return {
    'rounded-lg overflow-hidden': props.rounded,
    'cursor-pointer hover:opacity-90 transition-opacity': props.clickable,
    'group': props.showCaption
  }
})

const handleNavigate = (index: number) => {
  currentImageIndex.value = index
}

const removeFile = async (image: Image) => {  
  const response = await api.post(
      "/clients/inventory/item-management/items/photo-status",
      { id: image.uid, 
        items_id: props.item.id
       }
    );
    if (response.success == true) {
      Toast.show("Photo Removed successfully");
      emit('removed', response.data)
    }
}

// load sources initially and whenever images change
onMounted(() => {
  props.images.forEach(img => loadImageSrc(img))
})

watch(
  () => props.images,
  (newImages) => {
    if (!newImages) return
    newImages.forEach(img => loadImageSrc(img))
  },
  { deep: true }
)
</script>

<template>
  <div>
    <div
      class="grid w-full"
      :style="{
        gridTemplateColumns: gridColumns,
        gap: gridGap
      }"
    >
     <div
        v-for="image in images"
        :key="image.id"
        :class="imageContainerClasses"       
      >
      <AsyncImage :src="getImageUrl(image)" :alt="image.alt || ''" :readonly="false" @remove="removeFile(image)"/>

      </div>
      <!-- <div
        v-for="image in images"
        :key="image.id"
        :class="imageContainerClasses"       
      >
        <div @click="handleImageClick(image)">
        <div
          class="relative w-full h-0"
          :style="{
            paddingBottom: props.aspectRatio ? `calc(100% / (${props.aspectRatio}))` : '100%'
          }"
        >
          <img
            :src="getImageUrl(image)"
            :alt="image.alt || ''"
            @error="handleImageError"
            class="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
          />
          
          <div
            v-if="showCaption && getCaption(image)"
            class="absolute inset-x-0 bottom-0 p-2 bg-black/50 text-white text-sm opacity-0 group-hover:opacity-100 transition-opacity"
          >
            {{ getCaption(image) }}
          </div>          
        </div>
        </div>
        <button
            @click="removeFile(image)"
            class="p-1 bg-red-500 rounded-full text-white hover:bg-red-800 flex-shrink-0"
          >
            <IconComponent name="Trash" class="w-4 h-4" />
          </button>
      </div> -->
    </div>

    <!-- Image Modal -->
    <ImageModal
      v-if="showModal"
      :images="images.map(img => ({ 
        ...img, 
        src: getImageUrl(img),
        caption: getCaption(img)
      }))"
      :current-index="currentImageIndex"
      @close="showModal = false"
      @navigate="handleNavigate"
    />
  </div>
</template>

<style scoped>
@media (max-width: 1024px) {
  .grid {
    grid-template-columns: repeat(3, 1fr) !important;
  }
}

@media (max-width: 768px) {
  .grid {
    grid-template-columns: repeat(2, 1fr) !important;
  }
}

@media (max-width: 480px) {
  .grid {
    grid-template-columns: repeat(1, 1fr) !important;
  }
}
</style>